import React, { useState, useCallback, useEffect } from "react";
import {
  TableContainer,
  IconButton,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CustomDatagrid from "../../../components/datagrid";
import { makeStyles } from "@mui/styles";
import RoleDetails from "../role_details";
import userApi from "../../../services/users";
import roleApi from "../../../services/role";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../../redux/loader/actions";
import { showSnackbar } from "../../../redux/snackbar/actions";
import {
  getDecryptedCookie,
  encryptAES,
} from "../../../utils";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

const useStyles = makeStyles({
  animatedButton: {
    background: "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
    color: "#fff",
    padding: "10px 30px",
    fontSize: "16px",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "5px",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      background: "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
    },
  },
  clickable: {
    cursor: "pointer",
    color: "#3f51b5",
    textDecoration: "underline",
  },
});

const UserList = ({
  users,
  onEditUser,
  onDeleteUser,
  lockedRoles,
  handleToggleStatus,
  handleToggleLock,
  dummyUser,
  setUserList,
  userList,
  userDatabyID,
  setUserDatabyID,
  editUserById,
  rowCount,
  setSearchparams,
  paginationModel,
  setPaginationModel,
  fetchUsers,
}) => {
  const classes = useStyles();

  const menuItems = useSelector((state) => state.menuReducer.items);
  const createMenuItem = menuItems?.find(
    (item) => item.name.toLowerCase() === "users"
  );
  const userId = getDecryptedCookie("uuid");
  const isDisabled = createMenuItem?.access_type === "read_write";
  const dispatch = useDispatch();
  const [filters, setFilters] = useState({
    username: "",
    mobile_number: "",
    access_type: "",
    role_uuid: "",
    status: "",
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isLocked, setIsLocked] = useState(true);

  const toggleLock = (id) => {
    dispatch(showLoader());
    const onSuccess = (res) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: res?.data?.msg,
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        })
      );
      setIsLocked(false);
      fetchUsers();
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg,
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      setIsLocked(true);
    };
    userApi?.userUnlock(id).then(onSuccess, onFailure);
  };

  const [roleList, setRoleList] = useState();
  const [sortOrder, setSortOrder] = useState([{ field: "id", sort: "desc" }]);

  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value,
    });
  };

  const handleSearch = () => {
    setSearchparams(filters);
  };

  const handleReset = () => {
    setFilters({
      username: "",
      mobile_number: "",
      access_type: "",
      role_uuid: "",
      status: "",
    });
    setSearchparams({});
  };

  const handleOpenModal = (user) => {
    setSelectedUser(user);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedUser(null);
  };

  const getAllRoles = () => {
    dispatch(showLoader());

    const onSuccess = (res) => {
      dispatch(hideLoader());
      showSnackbar({
        message: "Plan Created Successfully",
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        variant: "success",
      });
      setRoleList(res?.data?.data);
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      console.error("Failed to fetch data", err);
    };

    roleApi.getAllRole().then(onSuccess).catch(onFailure);
  };

  useEffect(() => {
    getAllRoles();
  }, []);

  const updateUserStatus = async (userid, currentStatus) => {
    const data = { status: !currentStatus };
    const encryptedData = encryptAES(data);
    dispatch(showLoader());
    try {
      const response = await userApi.userEdit(userid, encryptedData);
      if (response?.data?.status) {
        fetchUsers();
      } else {
        dispatch(showSnackbar({ message: response.data.message }));
      }
    } catch (error) {
      console.error("Error updating user status:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const UserListRows = Array.isArray(userList)
    ? userList.map((data, index) => {
      const sno_id =
        paginationModel.page * paginationModel.pageSize + (index + 1);

      return {
        id: data?.uuid,
        sno_id: sno_id,
        name: data?.username,
        email: data?.email,
        mobile_number: data?.mobile_number,
        user_type:
          data?.bocUserInfo[0]?.product_access &&
            data?.bocUserInfo[0]?.tagitstore_access
            ? "Both"
            : data?.bocUserInfo[0]?.product_access
              ? "Product User"
              : data?.bocUserInfo[0]?.tagitstore_access
                ? "Tagitstore BOC"
                : "None",
        action_id: data?.uuid,
        login:
          data?.bocUserInfo[0]?.login_by === "email"
            ? data?.email
            : data?.mobile_number,
        status: data?.status,
        role: data?.roleMappings[0]?.role?.role || "-",
        failed: data?.failed_attempts,
        isLocked: data?.failed_attempts >= 5,
      };
    })
    : [];

  const columns = [
    {
      field: "sno_id",
      headerName: "S.No",
      width: 100,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "name",
      headerName: "Name",
      width: 150,
      headerAlign: "left",
      align: "center",
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 250,
      headerAlign: "left",
      align: "center",
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "mobile_number",
      headerName: "Mobile Number",
      width: 200,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "login",
      headerName: "Login ID",
      width: 150,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "user_type",
      headerName: "User Type",
      width: 150,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const capitalizedUserType = params.row.user_type
          ? params.row.user_type.charAt(0).toUpperCase() +
          params.row.user_type.slice(1)
          : "";
        return capitalizedUserType;
      },
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "role",
      headerName: "Role",
      width: 150,
      headerAlign: "left",
      align: "center",
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const capitalizedUserType = params.row.role
          ? params.row.role.charAt(0).toUpperCase() + params.row.role.slice(1)
          : "";
        return capitalizedUserType;
      },
    },
    ...(isDisabled
      ? [
        {
          field: "status",
          headerName: "Status",
          width: 150,
          headerAlign: "left",
          align: "center",
          disableColumnMenu: true,
          sortable: false,
          renderCell: (params) => {
            return (
              <Switch
                checked={params.row.status === true}
                onChange={() => {
                  updateUserStatus(
                    params.row.action_id,
                    // params.row.roleid,
                    params.row.status === true
                  );
                }}
                disabled={userId === params.row.id}
                color={params.row.status === true ? "primary" : "default"}
                inputProps={{ "aria-label": "controlled" }}
              />
            );
          },
        },
      ]
      : [
        {
          field: "status",
          headerName: "Status",
          width: 150,
          headerAlign: "left",
          align: "center",
          sortable: false,
          disableColumnMenu: true,
          renderCell: (params) => {
            return (
              <Chip
                variant="outlined"
                size="small"
                label={params?.row?.status ? "Active" : "In-Active"}
                color={params?.row?.status ? "success" : "error"}
              />
            );
          },
        },
      ]),
    ...(isDisabled
      ? [
        {
          field: isDisabled ? "action" : null,
          headerName: isDisabled ? "Action" : null,
          sortable: false,
          width: isDisabled ? 150 : null,
          headerAlign: "left",
          disableColumnMenu: true,
          align: "left",
          hide: isDisabled,
          renderCell: (params) =>
            params.row.id !== userId ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EditIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => editUserById(params.row.action_id)}
                />
                <IconButton
                  onClick={() => toggleLock(params.row.id)}
                  disabled={!params.row.isLocked}
                >
                  {params.row.isLocked ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </Box>
            ) : null,
        },
      ]
      : []),
  ];

  return (
    <Box mt={2}>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item md={4} xs={12}>
              <TextField
                name="username"
                label="Name"
                variant="outlined"
                fullWidth
                value={filters.username}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField
                name="mobile_number"
                label="Mobile Number"
                variant="outlined"
                fullWidth
                value={filters.mobile_number}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  name="access_type"
                  value={filters.access_type}
                  onChange={handleFilterChange}
                  label="User Type"
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                  <MenuItem value="tagitstore_access">Tagitstore BOC</MenuItem>
                  <MenuItem value="product_access">Products</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={4} xs={12}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role_uuid"
                  value={filters.role_uuid}
                  onChange={handleFilterChange}
                  label="Role"
                >
                  <MenuItem value="">None</MenuItem>
                  {Array.isArray(roleList) &&
                    roleList.map((data, index) => (
                      <MenuItem key={index} value={data?.uuid}>
                        {data?.role.charAt(0).toUpperCase() +
                          data?.role.slice(1)}{" "}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={4} xs={12}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  label="Status"
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={12} xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ marginRight: 2 }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  className={classes.animatedButton}
                  onClick={handleSearch}
                  sx={{ marginRight: 2 }}
                >
                  Search
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card elevation={3} sx={{ marginTop: "15px" }}>
        <TableContainer>
          {userList.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ padding: "10px" }}
            >
              <Typography variant="subtitle1" color="textSecondary">
                No user Found
              </Typography>
            </Box>
          ) : (
            <CustomDatagrid
              rows={UserListRows}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              rowCount={rowCount}
              onSortModelChange={setSortOrder}
              disableSelectionOnClick={true}
              checkboxSelection={false}
              pagination
              pageSizeOptions={[5, 10, 20]}
            />
          )}
        </TableContainer>
      </Card>

      <RoleDetails
        open={openModal}
        onClose={handleCloseModal}
        user={selectedUser}
      />
    </Box>
  );
};

export default UserList;
